/**
 * 表单定义。
 *
 * 所有表单都是**纯前端 + mailto 兜底**：填完在浏览器本地拼一封邮件交给邮箱客户端，
 * 或一键复制正文。没有任何上传逻辑，因此页面上也就不存在"提交中"这类状态。
 */

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'select' | 'textarea';
  required: boolean;
  placeholder?: string;
  hint?: string;
  options?: readonly string[];
  /** 跨整行的宽字段（textarea、说明性 hint） */
  wide?: boolean;
}

export const TRIAL_FIELDS: readonly FormField[] = [
  { name: 'name', label: '称呼', type: 'text', required: true, placeholder: '请问如何称呼您' },
  { name: 'organization', label: '组织', type: 'text', required: false, placeholder: '公司、团队或学校（个人可留空）' },
  {
    name: 'email',
    label: '联系邮箱',
    type: 'email',
    required: true,
    placeholder: 'name@example.com',
    hint: '仅用于回复本次来信，不会进入任何名单。',
  },
  {
    name: 'scale',
    label: '机体规模',
    type: 'select',
    required: true,
    options: ['1 台', '2 – 5 台', '6 – 20 台', '20 台以上', '暂无机体，希望先了解平台'],
  },
  {
    name: 'intent',
    label: '希望了解',
    type: 'select',
    required: true,
    options: ['平台试用与接入', '企业定制 SaaS', '私有化部署', '自研机体接入', '其他合作'],
  },
  {
    name: 'message',
    label: '场景说明',
    type: 'textarea',
    required: false,
    wide: true,
    placeholder: '机体型号、现有工具链、希望解决的问题等。说明越具体，回复越有针对性。',
  },
];

export const CONTACT_FIELDS: readonly FormField[] = [
  { name: 'name', label: '称呼', type: 'text', required: true, placeholder: '请问如何称呼您' },
  {
    name: 'email',
    label: '联系邮箱',
    type: 'email',
    required: true,
    placeholder: 'name@example.com',
    hint: '仅用于回复本次来信，不会进入任何名单。',
  },
  {
    name: 'topic',
    label: '来意',
    type: 'select',
    required: true,
    options: ['平台能力咨询', '接入与联调', '企业定制与私有化', '合作与投资', '其他'],
  },
  {
    name: 'message',
    label: '想说的话',
    type: 'textarea',
    required: true,
    wide: true,
    placeholder: '请直接说明，无需客套。',
  },
];

export type FormValues = Record<string, string>;

/** 联系表单的邮件标题用语，与试用申请区分开，便于收件时分类。 */
export const CONTACT_MAIL_HEADING = 'Havorix™ 联系与咨询';
export const CONTACT_MAIL_SUBJECT_PREFIX = '[咨询] Havorix™';

/** 把表单值拼成邮件正文；键名到中文标签的映射来自字段定义本身。 */
export function buildMailBody(
  heading: string,
  fields: readonly FormField[],
  values: FormValues,
): string {
  const lines = fields
    .map((field) => {
      const value = (values[field.name] ?? '').trim();
      return value ? `${field.label}：${value}` : null;
    })
    .filter((line): line is string => line !== null);

  return [heading, '—'.repeat(20), ...lines].join('\n');
}

export function buildMailSubject(prefix: string, values: FormValues): string {
  const parts = [values['intent'], values['topic'], values['scale'], values['organization'], values['name']]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? `${prefix} · ${parts.join(' · ')}` : prefix;
}
